import EventBus from "eventing-bus"
import gsap from "gsap"
import { constants, getCamera, getModelsArr, getCurrBoard, getCurrSection, setCurrBoard, getSwipeDirection } from "../model"

const spacing = 2,
  THIS_SECTION_NUM = 0

let isBoardAnimating = false,
  isFirstTimeThroughSection0 = true,
  boardAddedToGroup = false

/**
 * Event Listeners
 */

const handleSectionChanged = () => {
  switch (getCurrSection()) {
    case 0:
      moveBoardsInitialPosition()
      isFirstTimeThroughSection0 = false
      break
    case 1:
      moveToTrucks()
      break
    case 2:
      moveToWheels()
      break
    case 3:
      moveToFinal()
      break
  }
}

const handleBoardChanged = () => {
  if (getCurrSection() === THIS_SECTION_NUM) moveBoardsLeftOrRight()
}

const handleSwipeDetected = () => {
  if (isBoardAnimating) return

  if (getSwipeDirection() === "left") {
    setCurrBoard(getCurrBoard() - 1)
  } else {
    setCurrBoard(getCurrBoard() + 1)
  }
  isBoardAnimating = true
  setTimeout(() => {
    isBoardAnimating = false
  }, 800)
}

const handleProductZoomClicked = (bool) => {
  if (bool) moveToZoomView()
  else moveToSummaryResting()
}

const handleWheelChanged = () => {
  moveToWheels()
}

EventBus.on(constants.SECTION_CHANGED, handleSectionChanged)
EventBus.on(constants.BOARD_CHANGED, handleBoardChanged)
EventBus.on(constants.SWIPE_DETECTED, handleSwipeDetected)
EventBus.on(constants.PLAY_PRODUCT_ZOOM, handleProductZoomClicked)
EventBus.on(constants.WHEEL_CHANGED, handleWheelChanged)

/**
 * Fn
 */
const moveBoardsInitialPosition = () => {
  getModelsArr().map((d, i) => {
    const startingBoard = getCurrBoard(),
      group = d.group,
      camera = getCamera(),
      posFromCurr = i - startingBoard,
      defaultScale = 0.6,
      x = posFromCurr * spacing,
      scale = defaultScale - Math.abs(posFromCurr) * 0.06

    let durationGroup = isFirstTimeThroughSection0 ? 1 : 0.5,
      durationCamera = isFirstTimeThroughSection0 ? 3 : 2,
      delay = isFirstTimeThroughSection0 ? 1000 + i * 400 : i * 100

    group.visible = false

    gsap.fromTo(camera.position, { x: 10, y: 0, z: 50 }, { duration: durationCamera, x: 0, y: 0, z: 20, ease: "power3.out" })

    setTimeout(() => {
      group.visible = true

      gsap.fromTo(
        group.rotation,
        { x: 0, y: Math.PI * 2, z: 0 },
        { duration: durationGroup, x: 0, y: 0, z: 0, ease: "power3.out" }
      )
      gsap.set(group.position, { x, y: 0.1, z: 0 })
      gsap.fromTo(
        group.scale,
        { x: 0, y: 0, z: 0 },
        { x: scale, y: scale, z: scale, duration: durationGroup, ease: "power3.out" }
      )
    }, delay)
  })
}

const moveBoardsLeftOrRight = () => {
  getModelsArr().map((d, i) => {
    const board = d.group,
      camera = getCamera(),
      posFromCurr = d.id - getCurrBoard(),
      defaultScale = 0.6,
      x = posFromCurr * spacing,
      scale = defaultScale - Math.abs(posFromCurr) * 0.06

    let duration = null,
      delay = i * 400

    gsap.to(camera.position, { duration: 2, x: 0, y: 0, z: 20, ease: "power3.inOut" })

    setTimeout(() => {
      duration = 1
      gsap.to(board.position, { duration, x, y: 0.1, z: 0, ease: "power3.inOut" })
      gsap.to(board.scale, { duration, x: scale, y: scale, z: scale, ease: "power3.inOut" })
    }, (delay *= 0.08))
  })
}

const moveToTrucks = () => {
  const board = getModelsArr()[getCurrBoard()].group,
    camera = getCamera(),
    duration = 1

  gsap.to(camera.position, { duration, x: 0, y: 0, z: 9, ease: "power2.inOut" })
  gsap.to(board.rotation, { duration, x: -1.56, z: 0.1, ease: "power2.inOut" })
  gsap.to(board.position, { duration, x: -0.17, y: -1.1, z: 0.6, ease: "power2.inOut" })
  animateOutOtherBoards()
}

const moveToWheels = () => {
  const group = getModelsArr()[getCurrBoard()].group,
    camera = getCamera()

  let duration = 2

  gsap.to(camera.position, { duration, x: 0, y: 0, z: 7, ease: "power2.inOut" })
  gsap.to(group.rotation, { duration, x: 1.5, y: 0, z: -3, ease: "power2.inOut" })
  gsap.to(group.position, { duration, x: 0.2, y: -0.9, z: -1, ease: "power2.inOut" })

  const delay = 3200
  duration = 1.1

  // Make sure you're in section 2
  setTimeout(() => {
    if (getCurrSection() === 2) {
      gsap.to(camera.position, { duration, x: -1, y: -1, z: 6, ease: "power3.inOut" })
      gsap.to(group.rotation, { duration, x: -1.5, y: 0, z: -1.6, ease: "power3.inOut" })
      gsap.to(group.position, { duration, x: 1.2, y: -1.3, z: 2, ease: "power3.inOut" })
    }
  }, 3200)

  animateOutOtherBoards()
}

const moveToFinal = () => {
  const group = getModelsArr()[getCurrBoard()].group,
    camera = getCamera()

  let duration = 1.7,
    delay = 1.7

  gsap.to(camera.position, { duration, x: 0, y: 0, z: 18, ease: "power2.inOut" })
  gsap.to(group.rotation, { duration, x: 0, y: Math.PI / 4, z: 0, ease: "power2.inOut" })
  gsap.to(group.position, { duration, x: 0, y: -0.4, z: 2, ease: "power2.inOut" })

  duration = 2.2

  gsap.to(camera.position, { duration, x: 0, y: 0, z: 16, delay, ease: "power2.inOut" })
  gsap.to(group.rotation, { duration, x: 0, y: -(Math.PI / 4) * Math.PI * 2.8, z: 0, delay, ease: "power2.inOut" })
  gsap.to(group.position, { duration, x: 0, y: -0.4, z: 2, delay, ease: "power2.inOut" })

  setTimeout(() => {
    moveToSummaryResting()
  }, 4200)

  animateOutOtherBoards()
}

const moveToSummaryResting = () => {
  const group = getModelsArr()[getCurrBoard()].group,
    camera = getCamera(),
    duration = 1.3

  gsap.to(group.rotation, { duration, x: 0, y: -Math.PI * 2, z: 0, ease: "power2.inOut" })
  gsap.to(group.position, { duration, x: 0, y: -3.3, z: 0, ease: "power2.inOut" })
  gsap.to(camera.position, { duration, x: 0, y: 0, z: 7, ease: "power2.inOut" })
}

const moveToZoomView = () => {
  const group = getModelsArr()[getCurrBoard()].group,
    camera = getCamera(),
    duration = 2.2

  const screenWide = window.innerWidth > window.innerHeight
  let rotationZ = null,
    cameraZoom = null

  if (screenWide) {
    rotationZ = -Math.PI / 2
    cameraZoom = 7
  } else {
    rotationZ = 0
    cameraZoom = 12
  }

  gsap.to(group.rotation, { duration, x: 0, y: 0, z: rotationZ, ease: "power3.inOut" })
  gsap.to(group.position, { duration, x: 0, y: 0, z: 0.3, ease: "power3.inOut" })
  gsap.to(camera.position, { duration, x: 0, y: 0, z: cameraZoom, ease: "power3.inOut" })
}

const animateOutOtherBoards = () => {
  getModelsArr().map((d, i) => {
    const model = d.group
    if (d.id !== getCurrBoard()) {
      setTimeout(() => {
        gsap.to(model.scale, { duration: 0.5, x: 0, y: 0, z: 0, ease: "power2.in" })
      }, i * 50)
    }
  })
}
